import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown, Grid, Icon, Image, Label, StrictDropdownProps, TextArea } from 'semantic-ui-react'
import DatepickerComponent from '../../../../components/Datepicker/Datepicker'
import { IUserDetail } from '../../../../types/user'
import { checkRole, checkTeamMemberOrAdminStatus } from '../../../../utils/checkRole'
import { currencyFormat } from '../../../../utils/currencyFormat'
import { durationToHours } from '../../../../utils/durationToHours'
import { statusColor } from '../../../../utils/statusColor'
import { IEmployee } from '../../../settings/types/employee'
import { IDirectCost } from '../../types/directAndPassTroughCosts'
import { IProject, IProjectLabel, IProjectPhase, projectStatus } from '../../types/project'
import { TaskStatus } from '../../types/projectActivity'
import TeamGroupComponent from '../list/TeamGroupComponent'
import './ActivityDetails.css'
//svg files
import { useHistory } from 'react-router-dom'
import department from '../../../../assets/svg/act_detail_department.svg'
import description from '../../../../assets/svg/act_detail_description.svg'
import member from '../../../../assets/svg/act_detail_member.svg'
import labels from '../../../../assets/svg/act_detail_noun_label.svg'
import phase from '../../../../assets/svg/act_detail_phase.svg'
import status from '../../../../assets/svg/act_detail_status.svg'
import timelines from '../../../../assets/svg/act_detail_timelines.svg'
import total_hours from '../../../../assets/svg/act_detail_total_hours.svg'
import budget from '../../../../assets/svg/acts_detail_expenditures.svg'
import completed from '../../../../assets/svg/completed_label.svg'
import progress from '../../../../assets/svg/in_progress_label.svg'
import upcoming from '../../../../assets/svg/upcoming_label.svg'
import { useWindowSize } from '../../../../hooks/useWindowSize'



interface StateProps {
  cost: IDirectCost
  costStatus: TaskStatus[0]
  project: IProject
  employees: IEmployee[]
  costName: string
  user: IUserDetail | null
}

interface DispatchProps {
  updateCost: (projectId: string | number, cost: IDirectCost, closedDatepicker: boolean) => void
  setActiveBudgetTab: (tab: string) => void
  onCloseModal: () => void
}

type Props = StateProps & DispatchProps

const DirectAndPassTroughDetailsOverviewComponent = (props: Props): JSX.Element => {
  const { user, cost, costStatus, project, employees, costName, updateCost, setActiveBudgetTab, onCloseModal } = props

  const [costState, setCostState] = useState({ ...cost, statusColor: (status: string) => statusColor(status) })
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false)
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false)
  const [editRight, setEditRight] = useState(false)
  const timer = useRef<number>(0)

  const _history = useHistory()
  const _height = useWindowSize().height

  const setBookedAdmins = useCallback((bookedEmps: IEmployee[]) => {
    return bookedEmps.map(emp =>
      ({ ...emp, is_project_admin: project.admins ? project.admins.find(a => +a.id === +emp.id) ? true : false : false }))
  }, [project])

  useEffect(() => {
    const _cost = {
      ...cost,
      statusColor: (status: string) => statusColor(status),
      booked_employees: setBookedAdmins(cost.booked_employees ? cost.booked_employees : [])
    }
    setCostState(_cost)
  }, [cost, setCostState, setBookedAdmins])

  useEffect(() => {
    if (user) {
      setEditRight(checkRole(user, [15]) && checkTeamMemberOrAdminStatus(user, project))
    }
  }, [user, project])

  useEffect(() => {
    const end_date = costStatus === 'completed' ? moment(new Date()).format('YYYY-MM-DD') : null
    if (costState.status !== costStatus) {
      setCostState(prev => { return { ...prev, status: costStatus, end_date: end_date } })
    }
  }, [costStatus, costState.status, setCostState])

  useEffect(() => {
    setCostState(prev => { return { ...prev, offer_position: costName } })
  }, [costName, setCostState])

  // change dates

  const onInputClickStart = () => {
    if (openStartDatePicker) {
      setOpenStartDatePicker(false)
    } else {
      setOpenEndDatePicker(false)
      setOpenStartDatePicker(true)
    }
  }

  const onInputClickEnd = () => {
    if (openEndDatePicker) {
      setOpenEndDatePicker(false)
    } else {
      setOpenStartDatePicker(false)
      setOpenEndDatePicker(true)
    }
  }

  const onClickOutsideStart = () => {
    setOpenStartDatePicker(false)
  }

  const onClickOutsideEnd = () => {
    setOpenEndDatePicker(false)
  }

  const changeStartDate = (date: Date) => {
    if (moment(date).isSameOrBefore(moment(costState.end_date))) {
      const _start = date ? moment(date).format('YYYY-MM-DD') : null
      updateCost(project.id, { ...costState, start_date: _start }, false)
      setCostState({ ...costState, start_date: _start })
      setOpenStartDatePicker(false)
    }
  }

  const changeEndDate = (date: Date) => {
    if (moment(date).isSameOrAfter(moment(costState.start_date))) {
      const _end = date ? moment(date).format('YYYY-MM-DD') : null
      updateCost(project.id, { ...costState, end_date: _end }, false)
      setCostState({ ...costState, end_date: _end })
      setOpenEndDatePicker(false)
    }
  }

  const changeEmployees = (data: StrictDropdownProps) => {
    if (data && data.value) {
      const value = data.value.toString()
      const _employees: IEmployee[] = []
      value.split(',').forEach(d => {
        employees.forEach(e => {
          if (e.email === d && !_employees.includes(e)) {
            _employees.push(e)
          }
        })
      })
      updateCost(project.id, { ...costState, assigned_employees: _employees }, openEndDatePicker)
      setCostState(prev => { return { ...prev, assigned_employees: _employees } })
    }
  }

  const changeLabels = (data: StrictDropdownProps) => {
    if (data && data.value) {
      const value = data.value.toString()
      const _labels: IProjectLabel[] = []
      value.split(',').forEach(l => {
        project.labels.forEach(pl => {
          if (+pl.id === +l && !_labels.includes(pl)) {
            _labels.push(pl)
          }
        })
      })
      updateCost(project.id, { ...costState, labels: _labels }, openEndDatePicker)
      setCostState(prev => { return { ...prev, labels: _labels } })
    }
  }

  // change input values
  const inputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>, key: string) => {
    const value = key !== 'description' ? event.target.value.replace(/[^0-9]+/g, '') : event.target.value
    const _value = key !== 'description' ? +value : value
    window.clearTimeout(timer.current)
    const _timer = window.setTimeout(() => {
      updateCost(project.id, { ...costState, [key]: _value }, openEndDatePicker)
    }, 1000)
    timer.current = _timer
    setCostState(prev => { return { ...prev, [key]: _value } })
  }

  const renderTimelinesContent2 = () => {
    return (
      <div style={{ display: 'flex', width: '70%', marginTop: '-.3rem' }}>
        <DatepickerComponent
          className='act-details-datepicker'
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd/MMM/yy'
          disabled={!editRight}
          inputWidth='6.5rem'
          inputBorderSmall={false}
          open={openStartDatePicker}
          onClickOutside={() => onClickOutsideStart()}
          onInputClick={() => onInputClickStart()}
          onChange={(date: Date) => changeStartDate(date)}
          selected={costState.start_date ? moment(costState.start_date).toDate() : undefined}
          placeholder='Start Date'
        />
        <DatepickerComponent
          className='act-details-datepicker'
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd/MMM/yy'
          disabled={!editRight}
          inputWidth='6.5rem'
          open={openEndDatePicker}
          onClickOutside={() => onClickOutsideEnd()}
          onInputClick={() => onInputClickEnd()}
          onChange={(date: Date) => changeEndDate(date)}
          selected={costState.end_date ? moment(costState.end_date).toDate() : undefined}
          placeholder='End Date'
        />
      </div>
    )
  }

  const renderStatusContent = () => {
    return (
      <Label
        image
        style={{
          backgroundColor: costState.statusColor(costState.status).background,
          color: costState.statusColor(costState.status).color,
          borderRadius: '8px',
          height: '26px'
        }}>
        <Image
          src={
            costState.status === 'completed' ? completed
              : costState.status === 'in_progress' ? progress
                : costState.status === 'upcoming' ? upcoming
                  : undefined
          }
          style={{ maxWidth: '12px', marginLeft: '2px' }}
        />
        {projectStatus.find(s => s.key === costState.status)?.name}
      </Label>
    )
  }

  const renderMemberDropdown = (empLen: boolean) => {
    const options = employees.filter(e => e.id !== '').map(e => {
      return {
        key: e.id,
        text: e.username,
        value: e.email
      }
    })
    return (
      <Dropdown
        id='member-dropdown'
        value={costState.assigned_employees ? costState.assigned_employees.map(e => e.email) : []}
        onChange={(e, data) => changeEmployees(data)}
        icon={<Icon style={{ display: 'none' }} />}
        placeholder='Select Members'
        multiple={true}
        selection={true}
        fluid={true}
        closeOnChange={false}
        options={options}
        style={{ display: empLen || !project.active ? 'none' : '', background: '#F9FBFC', border: 'none', zIndex: 11, height: 'auto', marginTop: '-0.8rem', padding: '.2rem 0 .2rem .2rem', borderRadius: '6px' }}
        disabled={costStatus === 'completed' || !project.active}
      />
    )
  }

  const renderMemberContent = () => {
    return (
      <div id='member-component' style={{ padding: '.5rem 0 .5rem 0', marginTop: '-.3rem' }}>
        <div id={editRight ? 'member-icons' : ''} style={{ display: costState.assigned_employees.length > 0 ? 'flex' : 'none', marginTop: '-8px', marginLeft: '.3rem' }}>
          <TeamGroupComponent size='32px' toLeft={3} key={`${project.id}-${costState.assigned_employees?.length}`} team={costState.assigned_employees ?? []} />
        </div>
        {editRight && renderMemberDropdown(costState.assigned_employees.length > 0)}
      </div>
    )
  }

  const renderLabelsContent = () => {
    const options = project.labels.map((l, idx) => {
      return {
        key: idx,
        text: (
          <Label
            key={idx}
            style={{
              background: l.color,
              color: '#172533',
              borderRadius: '8px',
              height: '26px',
              fontWeight: 500,
              marginTop: 0
            }}
          >
            <Icon name='hashtag' style={{ fontSize: '0.8rem', fontWeight: 500, color: '#172533', marginRight: '0.2rem' }} />{l.name}
          </Label>
        ),
        value: l.id
      }
    })
    return (
      <Dropdown
        floating={true}
        value={costState.labels ? costState.labels.map(l => l.id) : []}
        onChange={(e, data) => changeLabels(data)}
        className={'act-details-dropdown-labels'}
        icon={<Icon name='chevron down' style={{ display: 'none' }} />}
        placeholder='Allocate Labels'
        multiple={true}
        selection={true}
        fluid={true}
        closeOnChange={false}
        options={options}
        style={{ minHeight: '32px', padding: '0 4px', background: '#F9FBFC' }}
        disabled={!project.active || !editRight}
      />
    )
  }

  const renderDescriptionContent = () => {
    return (
      <div>
        <TextArea
          value={costState.description ? costState.description : ''}
          onChange={e => inputChange(e, 'description')}
          className={!project.active || !editRight ? 'act-disable-textarea' : 'act-overview-textarea'}
          style={{ resize: 'none', minWidth: '100%', maxHeight: '6rem', border: 'none', marginLeft: '.1rem', borderRadius: '6px', background: '#F9FBFC' }}
          placeholder='Add a description...'
        />
      </div>
    )
  }

  const renderPhaseLabel = (phase: IProjectPhase) => {
    return (
      <Label
        content={phase.name}
        style={{
          background: phase.color + '33',
          color: phase.color,
          borderRadius: '8px',
          height: '26px'
        }}
      />
    )
  }

  const handleGoToExternal = () => {
    if (!_history.location.pathname.includes('budget')) {
      _history.replace(`/projects/${project.id}/budget`, { tab: 'external costs' })
    }
    setActiveBudgetTab('external costs')
    onCloseModal()
  }

  const renderExpendituresContent = () => {
    const external = costState.external_expenditures ? costState.external_expenditures : 0.00
    const currency = currencyFormat(costState.expenditures ? +costState.expenditures + external : external).replace(/\s/g, '')
    const total_costs = currencyFormat(+costState.unit_price * +costState.quantity).replace(/\s/g, '')
    const total_external = currencyFormat(external).replace(/\s/g, '')
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '-.2rem' }}>
          <div style={{ marginTop: '-.5rem', fontSize: '24px', color: '#08D6A1', letterSpacing: '.25px', lineHeight: 0, display: 'flex', alignItems: 'baseline' }}>
            <p>{currency[0]} {currency.split(',')[0].replace(currency[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currency.split(',')[1]}</p>
            <p style={{ color: '#172533', fontSize: '18px', paddingLeft: '.3rem', fontWeight: 'bold' }}>/</p>
          </div>
          <p style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '.3rem' }}>{total_costs[0]} {total_costs.split(',')[0].replace(total_costs[0], '')}</p>
          <p style={{ color: '#BEC8C3', marginRight: '.3rem', fontSize: '18px', fontWeight: 'bold' }}>,{total_costs.split(',')[1]}</p>
        </div>
        {costState.external_expenditures ?
          <div style={{ marginTop: '-2rem', display: 'flex', fontWeight: 'bold', fontSize: '14px' }}>
            <p>{total_external[0]} {total_external.split(',')[0].replace(total_external[0], '')}</p>
            <p style={{ color: '#BEC8C3', marginRight: '.3rem' }}>,{total_external.split(',')[1]}</p>
            thereof External Costs <Icon onClick={handleGoToExternal} name='long arrow alternate right' style={{ marginLeft: '.2rem', color: '#08D6A1' }} />
          </div> : null}
      </div>
    )
  }

  const renderBillableAmountContent = () => {
    const billable_amount = costState.billable_amount ? costState.billable_amount : 0.00
    const currency = currencyFormat(+billable_amount).replace(/\s/g, '')
    const total_costs = currencyFormat(+costState.unit_price * +costState.quantity).replace(/\s/g, '')
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '-.2rem' }}>
          <div style={{ marginTop: '-.5rem', fontSize: '24px', color: '#08D6A1', letterSpacing: '.25px', lineHeight: 0, display: 'flex', alignItems: 'baseline' }}>
            <p>{currency[0]} {currency.split(',')[0].replace(currency[0], '')}</p>
            <p style={{ color: '#BEC8C3' }}>,{currency.split(',')[1]}</p>
            <p style={{ color: '#172533', fontSize: '18px', paddingLeft: '.3rem', fontWeight: 'bold' }}>/</p>
          </div>
          <p style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '.3rem' }}>{total_costs[0]} {total_costs.split(',')[0].replace(total_costs[0], '')}</p>
          <p style={{ color: '#BEC8C3', marginRight: '.3rem', fontSize: '18px', fontWeight: 'bold' }}>,{total_costs.split(',')[1]}</p>
        </div>
      </div>
    )
  }

  const renderTotalContent = () => {
    const _external_total = costState.new_total_hours ? costState.new_total_hours : 0.00
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '-.2rem' }}>
          <div style={{ fontSize: '24px', color: '#08D6A1', letterSpacing: '.25px', lineHeight: 0, display: 'flex', alignItems: 'baseline' }}>
            {durationToHours(+cost.current_hours)}h
            <p style={{ color: '#172533', fontSize: '18px', paddingLeft: '.3rem', fontWeight: 'bold' }}>/</p>
          </div>
          <div style={{ display: 'flex', fontSize: '18px', fontWeight: 'bold', marginLeft: '.2rem' }}>{_external_total ? _external_total : durationToHours(+costState.total_hours)}<p style={{ color: '#b7b7b7', marginLeft: '.1rem', lineHeight: '1.4rem' }}>h</p></div>
        </div>
        {costState.new_total_hours ?
          <div style={{ marginTop: '-.2rem', display: 'flex', fontWeight: 'bold', fontSize: '14px' }}>
            <p style={{ marginRight: '.3rem' }}>{durationToHours(_external_total)}h / {durationToHours(+costState.total_hours)}h</p>
            due to paid External Costs <Icon onClick={handleGoToExternal} name='long arrow alternate right' style={{ marginLeft: '.2rem', color: '#08D6A1' }} />
          </div> : null}
      </div>
    )
  }

  const renderColumns = () => {
    const columns = [
      { image: timelines, text: 'Timelines', content: (renderTimelinesContent2()) },
      { image: phase, margin: '.1rem', text: 'Phase', content: (renderPhaseLabel(costState.project_phase)) },
      { image: department, text: 'Department', content: (<div>{costState.department ? costState.department.name : '-'}</div>) },
      { image: total_hours, margin: '.2rem', text: 'Total Hours', content: (renderTotalContent()) },
      { image: status, text: 'Status', content: (renderStatusContent()) },
      { image: budget, margin: '.2rem', text: 'Expenditures', content: (renderExpendituresContent()) },
      { image: member, text: 'Member', content: (renderMemberContent()) },
      { image: labels, margin: '.1rem', text: 'Labels', content: (renderLabelsContent()) },
      { image: description, margin: '.1rem', text: 'Description', content: (renderDescriptionContent()) }
    ]
    if (project.billing_procedure === 'billing_on_actual' && columns.find(c => c.text === 'Billable Amount') === undefined) {
      columns.splice(6, 0, { image: '', margin: '.2rem', text: 'Billable Amount', content: (renderBillableAmountContent()) })
    }

    if (project.permissions === 'open' || project.time_tracking_perm.task_permission !== 'task_assignment') {
      if (project.billing_procedure === 'billing_on_actual') {
        columns.splice(7, 1)
      } else {
        columns.splice(6, 1)
      }
    }

    return columns.map((col, idx) => {
      return (
        <Grid.Row key={idx} style={{ height: col.text === 'Labels' ? 'auto' : col.text === 'Expenditures' && costState.external_expenditures ? '28px' : col.text === 'Total Hours' && costState.new_total_hours ? '28px' : '22px', margin: '0 0 31px', padding: 0 }}>
          <Grid.Column width={1} textAlign='left' style={{ padding: 0 }}><Image src={col.image} style={{ minWidth: '1rem', marginTop: col.margin ? col.margin : '' }} /></Grid.Column>
          <Grid.Column width={5} textAlign='left' style={{ padding: 0, color: '#172533', letterSpacing: '0', fontSize: '16px', fontWeight: 'bold', lineHeight: '19px' }}>{col.text}</Grid.Column>
          <Grid.Column width={10} textAlign='left' style={{ padding: 0 }}>{col.content}</Grid.Column>
        </Grid.Row>
      )
    })
  }

  return (
    <Grid
      verticalAlign='top'
      columns={3}
      style={{
        margin: '18px 16px 0',
        background: '#F9FBFC',
        flexDirection: 'column',
        minHeight: '50%',
        padding: _height < 500 ? '24px 15px 48px' : '24px 15px',
        borderRadius: '10px',
        flexWrap: 'nowrap',
        maxHeight: !(project.active && user && checkRole(user, [15]) && checkTeamMemberOrAdminStatus(user, project)) ? '581px' : `${_height - 286}px`,
        height: !(project.active && user && checkRole(user, [15]) && checkTeamMemberOrAdminStatus(user, project)) ? `${_height - 286}px` : '',
        overflow: 'hidden auto'
      }}>
      {renderColumns()}
    </Grid>
  )
}

export default DirectAndPassTroughDetailsOverviewComponent
